import { z } from "zod";
/**
 * Workspace slug: lower-case letters, digits and hyphens, 2–40 chars, must start
 * with a letter. Becomes part of the database name (`ws_<slug>` with hyphens as
 * underscores), so the character set is deliberately tight.
 */
export const WorkspaceSlug = z
    .string()
    .min(2)
    .max(40)
    .regex(/^[a-z][a-z0-9-]*$/, "slug must be lower-case letters, digits and hyphens, starting with a letter");
/**
 * requested → provisioning → active; failed is retried with backoff by the provisioner
 * until the attempt limit, then waits for an operator's retry (design 16.2).
 */
export const WorkspaceStatus = z.enum(["requested", "provisioning", "active", "archived", "failed"]);
export const Workspace = z.object({
    id: z.uuid(),
    slug: WorkspaceSlug,
    name: z.string().min(1).max(200),
    status: WorkspaceStatus,
    dbName: z.string(),
    dbHost: z.string(),
    schemaVersion: z.number().int().nonnegative(),
    autoProvisionUsers: z.boolean(),
    /** last provisioning error; cleared when the workspace becomes active */
    lastError: z.string().nullable(),
    /** provisioning attempts so far (reset by an operator's retry) */
    attempts: z.number().int().nonnegative(),
    requestedAt: z.coerce.date(),
    /** identity id of the operator who asked for it; null when created from the host CLI */
    requestedBy: z.uuid().nullable(),
    /** invited as system admin + member when the workspace is provisioned */
    initialAdminEmail: z.string().nullable(),
    /** starter definitions (deploy/templates/<name>.json) loaded when the workspace is provisioned */
    starterTemplates: z.array(z.string()),
    createdAt: z.coerce.date(),
});
/** Starter template name: a file stem in deploy/templates. */
export const StarterTemplateName = z.string().regex(/^[a-z][a-z0-9_-]{0,39}$/, "template names are lower-case file stems");
export const CreateWorkspaceInput = z.object({
    slug: WorkspaceSlug,
    name: z.string().min(1).max(200),
    /** invite this address as the workspace's first system admin */
    initialAdminEmail: z.email().optional(),
    /** starter definitions to load, e.g. ["agile", "phased"] */
    starterTemplates: z.array(StarterTemplateName).max(10).optional(),
});
/** A platform operator (design 16.1): installation-wide, no access inside any workspace. */
export const PlatformOperator = z.object({
    identityId: z.uuid(),
    issuer: z.string(),
    subject: z.string(),
    email: z.string(),
    displayName: z.string(),
    grantedAt: z.coerce.date(),
    grantedBy: z.uuid().nullable(),
});
/** Grant operator by OIDC issuer + subject; the identity is created if it has never logged in. */
export const GrantOperatorInput = z.object({
    issuer: z.string().min(1),
    subject: z.string().min(1),
    email: z.email(),
    displayName: z.string().min(1).max(200),
});
export const ControlActivityEntry = z.object({
    id: z.number().int(),
    at: z.coerce.date(),
    actorIdentityId: z.uuid().nullable(),
    action: z.string(),
    workspaceId: z.uuid().nullable(),
    details: z.record(z.string(), z.unknown()),
});
/** `GET /v1/admin/me`: who the caller is at installation level, and where they are a member. */
export const PlatformMe = z.object({
    identity: z
        .object({ id: z.uuid(), issuer: z.string(), subject: z.string(), email: z.string(), displayName: z.string() })
        .nullable(),
    isPlatformOperator: z.boolean(),
    memberships: z.array(z.object({ slug: WorkspaceSlug, name: z.string(), status: WorkspaceStatus })),
    /** open invitations for the caller's verified email in workspaces they are not yet in; joined on first visit */
    invitations: z.array(z.object({ slug: WorkspaceSlug, name: z.string(), role: z.enum(["member", "guest"]), systemAdmin: z.boolean() })),
});
/**
 * An invitation to a workspace by email (design 16.2). Accepted on the invitee's first
 * login, when their token carries the same email and the issuer vouches for it.
 */
export const Invitation = z.object({
    id: z.uuid(),
    workspaceSlug: WorkspaceSlug,
    email: z.string(),
    /** the workspace-scoped base role */
    role: z.enum(["member", "guest"]),
    /** also system admin (members only) */
    systemAdmin: z.boolean(),
    /** 'operator:<identity id>', 'user:<id>', 'service_account:<id>' or 'system' */
    invitedBy: z.string(),
    createdAt: z.coerce.date(),
    expiresAt: z.coerce.date(),
    acceptedAt: z.coerce.date().nullable(),
    revokedAt: z.coerce.date().nullable(),
    status: z.enum(["open", "accepted", "revoked", "expired"]),
});
export const CreateInvitationInput = z
    .object({
    email: z.email(),
    role: z.enum(["member", "guest"]).default("member"),
    systemAdmin: z.boolean().default(false),
    expiresInDays: z.number().int().min(1).max(90).default(14),
})
    .refine((v) => !(v.systemAdmin && v.role === "guest"), { message: "a guest cannot be a system admin", path: ["systemAdmin"] });
//# sourceMappingURL=workspace.js.map